/**
 * The same board, answering out loud.
 *
 * Every sound here is generated rather than loaded. A handful of oscillators
 * and one buffer of noise cost nothing to ship, survive having no network, and
 * cannot be blocked as a third-party fetch - and the alternative, a folder of
 * audio files, would be the only assets in the project that a player has to
 * download before the game can speak.
 *
 * The vocabulary is electromechanical, because the board already is: wires
 * taking current, tubes striking, sockets closing. Clicks are noise through a
 * narrow filter, which is what a contact actually sounds like; the cues that
 * carry news are pitched, because pitch is the one thing this channel has that
 * the motor does not, and it is what lets rising and falling mean something.
 *
 * What the moments are, and how they rank, is cues.js. This file only says what
 * each one sounds like.
 */

import { ACTIONS, weightOf } from './cues.js';
import { restore, persist } from './storage.js';

/** Where the mute lives. Namespaced like every other preference. */
export const MUTE_KEY = 'unkoad.muted';

const SETTINGS = ['on', 'off'];

/**
 * Every cue in the app as notes: when each starts, how high, how long.
 *
 * `kind: 'click'` is a contact closing - filtered noise, over before it can be
 * called a pitch. Everything else is a tone. `gain` is relative within the cue,
 * so a cue can rise or fall internally without touching how loud it is against
 * the others; that part is weight's job.
 *
 * Nothing outside this file holds a frequency or a duration, for the same
 * reason TAPS holds every pulse.
 */
export const TONES = {
    /** A peg into a socket. Five to a guess, so it stays a bare contact. */
    peg: [{ atMs: 0, kind: 'click', hz: 620, ms: 16 }],
    /** The fifth peg. The same contact a third up: the row is armed. */
    full: [{ atMs: 0, kind: 'click', hz: 810, ms: 20 }],
    /** Taking a peg back. The same click unlatching, so it falls. */
    clear: [
        { atMs: 0, kind: 'click', hz: 520, ms: 14 },
        { atMs: 46, kind: 'click', hz: 370, ms: 16, gain: 0.7 },
    ],
    /** A hold reaching the point where letting go would do something. */
    hold: [
        { atMs: 0, hz: 300, ms: 60, gain: 0.6 },
        { atMs: 40, hz: 470, ms: 90 },
    ],
    copied: [{ atMs: 0, hz: 880, ms: 70 }],

    /** The turn coming to you: two beats, the second higher. */
    turn: [
        { atMs: 0, hz: 440, ms: 90, gain: 0.75 },
        { atMs: 95, hz: 660, ms: 150 },
    ],
    /** A reaction. Dry and human-sized, so it reads as somebody, not a score. */
    poke: [
        { atMs: 0, kind: 'click', hz: 700, ms: 16 },
        { atMs: 70, kind: 'click', hz: 700, ms: 16 },
    ],

    /** Scored 0/0. One flat note that goes nowhere, like the guess. */
    dead: [{ atMs: 0, hz: 175, ms: 150 }],
    /** Colours found, none placed: two of the same, because nothing moved. */
    near: [
        { atMs: 0, hz: 440, ms: 80 },
        { atMs: 105, hz: 440, ms: 100 },
    ],
    /** Something in the right place, which is the only score that rises. */
    close: [
        { atMs: 0, hz: 440, ms: 80, gain: 0.8 },
        { atMs: 95, hz: 620, ms: 130 },
    ],
    /** Flat and equal, the shape of "no", on a reed that buzzes. */
    refused: [
        { atMs: 0, hz: 150, ms: 110, type: 'sawtooth' },
        { atMs: 150, hz: 150, ms: 110, type: 'sawtooth' },
    ],

    /**
     * The code going. The same shape the motor spells in rhythm, said in pitch
     * instead: a major arpeggio to the octave, each step louder as the gaps
     * close, then it holds - because nothing follows it.
     */
    win: [
        { atMs: 0, hz: 392, ms: 70, gain: 0.55 },
        { atMs: 50, hz: 523, ms: 70, gain: 0.7 },
        { atMs: 96, hz: 659, ms: 70, gain: 0.85 },
        { atMs: 144, hz: 784, ms: 80, gain: 0.95 },
        { atMs: 186, hz: 1046, ms: 420 },
    ],
    /** The opponent got there first. The same idea, running down. */
    lost: [
        { atMs: 0, hz: 392, ms: 130, gain: 0.9 },
        { atMs: 130, hz: 262, ms: 260 },
    ],
    /** Giving a game up: a breaker opening, with nothing answering it. */
    forfeit: [{ atMs: 0, hz: 90, ms: 220, type: 'triangle' }],
};

/** How loud the whole channel sits. Everything else is a fraction of this. */
const MASTER_GAIN = 0.18;

/** The span weight is spent across, so the peg is present and the win lands. */
const QUIETEST = 0.35;
const LOUDEST = 1;

/** Silence, near enough. Exponential ramps cannot reach an actual zero. */
const FLOOR = 0.0001;

/** Short enough to still read as a strike, long enough not to click on its own. */
const ATTACK_S = 0.003;

/** A narrow filter is what turns noise into a contact rather than a hiss. */
const CLICK_Q = 8;

/** One buffer of noise, reused. Longer than the longest click. */
const NOISE_S = 0.25;

const HEAVIEST = Math.max(...Object.values(ACTIONS).map((action) => action.weight));

/** How long a cue runs, from its first note to the end of its last. */
export function durationOf(name) {
    const notes = TONES[name];
    if (!notes) throw new Error(`sound: no preset named ${name}`);
    return Math.max(...notes.map((note) => note.atMs + note.ms));
}

/**
 * How loud a cue is, from how big its news is.
 *
 * Weight rather than a number per preset, so the two channels cannot come to
 * different conclusions about which cue matters more, and so tuning a cue's
 * importance is one edit in one file.
 */
export function gainFor(name) {
    const spread = (weightOf(name) - 1) / (HEAVIEST - 1);
    return QUIETEST + spread * (LOUDEST - QUIETEST);
}

/** Every moment has notes, or somewhere in the app plays silence. */
export function missingTones() {
    return Object.keys(ACTIONS).filter((name) => !TONES[name]);
}

/**
 * The ear channel.
 *
 * Unlike the motor this has a preference behind it, because sound carries to
 * people who did not choose to play anything. It is on by default - a channel
 * nobody discovers is one nobody wanted - and off is remembered, through the
 * same storage helpers the mode and the opponent go through, so a blocked
 * localStorage costs the player one tap rather than the page.
 *
 * No suppression rule, deliberately. Overlapping notes mix, which is what a
 * mixer is for, so the reason haptics guards the motor does not apply here: a
 * peg tapped during the win is a peg tapped during the win.
 */
export function createSounds({ scope = globalThis, storage = null } = {}) {
    const Context = (scope && (scope.AudioContext || scope.webkitAudioContext)) || null;

    let muted = restore(storage, MUTE_KEY, SETTINGS, 'on') === 'off';
    let ctx = null;
    let master = null;
    let noise = null;

    /**
     * The context, built on the first cue rather than on page load.
     *
     * A context created before the player has touched anything starts
     * suspended, and browsers count an unprompted one against the page. Built
     * inside the click that asks for it, it starts running.
     */
    function wake() {
        if (!Context) return null;

        if (!ctx) {
            ctx = new Context();
            master = ctx.createGain();
            master.gain.value = MASTER_GAIN;
            master.connect(ctx.destination);
            noise = noiseBuffer(ctx);
        }

        if (ctx.state === 'suspended' && typeof ctx.resume === 'function') {
            const resuming = ctx.resume();
            // Refused because there is still no user activation. Nothing waits
            // on this, and the next cue will ask again.
            if (resuming && typeof resuming.catch === 'function') resuming.catch(() => {});
        }

        return ctx;
    }

    return {
        /** Whether this browser can make a sound at all. */
        available: () => Context !== null,

        muted: () => muted,

        /** Sets the mute and remembers it, for this page and the next visit. */
        mute(value) {
            muted = Boolean(value);
            persist(storage, MUTE_KEY, muted ? 'off' : 'on');
            return muted;
        },

        enabled: () => Context !== null && !muted,

        play(name) {
            const notes = TONES[name];
            if (!notes) throw new Error(`sound: no preset named ${name}`);

            const context = wake();
            if (!context) return false;

            // Still suspended after asking, so the player has not interacted
            // yet. Dropping the cue rather than queueing it is the same answer
            // the autoplay policy is already giving, and it keeps a board left
            // open in a background tab from banking up every turn it missed.
            if (context.state === 'suspended') return false;

            const at = context.currentTime;
            const gain = gainFor(name);
            for (const note of notes) schedule(context, master, noise, note, at, gain);
            return true;
        },
    };
}

/** Lays one note onto the timeline. */
function schedule(ctx, master, noise, note, startAt, cueGain) {
    const at = startAt + note.atMs / 1000;
    const until = at + note.ms / 1000;
    const peak = Math.max(FLOOR, cueGain * (note.gain === undefined ? 1 : note.gain));

    // Ramped rather than switched, at both ends. A gain that steps straight to
    // its value clicks, and that click is not the one the preset asked for.
    const envelope = ctx.createGain();
    envelope.connect(master);
    envelope.gain.setValueAtTime(FLOOR, at);
    envelope.gain.exponentialRampToValueAtTime(peak, at + ATTACK_S);
    envelope.gain.exponentialRampToValueAtTime(FLOOR, until);

    const { source, out } = sourceFor(ctx, noise, note);
    out.connect(envelope);
    source.start(at);
    source.stop(until);
}

/** A note's raw sound, before the envelope shapes it. */
function sourceFor(ctx, noise, note) {
    if (note.kind !== 'click') {
        const osc = ctx.createOscillator();
        osc.type = note.type || 'sine';
        osc.frequency.value = note.hz;
        return { source: osc, out: osc };
    }

    const source = ctx.createBufferSource();
    source.buffer = noise;

    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = note.hz;
    band.Q.value = CLICK_Q;
    source.connect(band);

    return { source, out: band };
}

/** White noise, made once and struck repeatedly. */
function noiseBuffer(ctx) {
    const frames = Math.ceil(ctx.sampleRate * NOISE_S);
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
}
